import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApi } from '../../services/api/provider';
import { InfoTooltip } from '../InfoTooltip';
import { ProductService } from '../../services/api/domains/products/service';
import {
  ExternalLinkSource,
  ExternalLinkSuggestionResult,
} from '../../services/api/domains/products/types';
import { getStagedRecordKey } from '../../domains/products/adapters';
import {
  GRAY_200,
  GRAY_300,
  GRAY_500,
  GRAY_50,
  GRAY_900,
} from 'theme/grayScale';
import { COLOR_DANGER, COLOR_DANGER_TEXT } from 'theme/colors';

// ---------------------------------------------------------------------------
// ExternalLinkSuggestionsPane — issue #622, Sub-task 9.3 (High risk)
// ---------------------------------------------------------------------------

/** One `{source, record_id}` group this pane offers a checkbox for. */
export interface SuggestionGroupSelection {
  matched_field: string;
  display_name: string;
  record_ids: number[];
  /** Per-record display detail (issue #622 follow-up) — see `ExternalLinkSuggestionResult.records`. */
  records: Array<{ record_id: number } & Record<string, unknown>>;
}

interface ExternalLinkSuggestionsPaneProps {
  productId: number;
  source: ExternalLinkSource;
  sourceLabel: string;
  /** `(source, record_id)` keys already in the review list (search- or suggestion-added) — see `getStagedRecordKey`. */
  stagedKeys: Set<string>;
  onToggleSuggestionGroup: (
    group: SuggestionGroupSelection,
    checked: boolean
  ) => void;
}

/**
 * Per-variant fetch state. `variantName` is only known once THAT index's
 * own request has ever succeeded — a variant whose very first request fails
 * never learns its real name, so the table falls back to "Variant N" for
 * it (see `buildTableRows` below). Kept as null (not '') so that fallback
 * is an explicit, single decision point rather than scattered `|| 'Variant
 * N'` checks.
 */
interface VariantSlot {
  status: 'loading' | 'success' | 'error';
  variantName: string | null;
  results: ExternalLinkSuggestionResult[];
  errorMessage?: string;
}

type PaneStatus =
  'loading-first' | 'ready' | 'no-variants' | 'first-variant-error';

interface TableRow {
  key: string;
  /** Only set on the FIRST row of a variant's group — every continuation row leaves this null so the cell renders blank (the agreed table shape). */
  variantLabel: string | null;
  kind: 'suggestion' | 'failed';
  group?: ExternalLinkSuggestionResult;
  errorMessage?: string;
}

const DEFAULT_ERROR_MESSAGE =
  'Failed to load suggestions for this name variant.';

/**
 * PERF-003: caps how many `fetchVariant` requests are in flight at once — a
 * product with many name variants used to fire every remaining variant
 * concurrently, one HTTP request each, all at once. Jeff's framing: "start
 * at 3, test and adjust" — kept as a named constant for that reason.
 */
const MAX_CONCURRENT_VARIANT_REQUESTS = 3;

/**
 * Runs `worker` over `items` with at most `concurrency` calls in flight at
 * once. `worker` must never reject (settle its own errors internally) —
 * this queue has no error-handling of its own, it only tracks completion to
 * know when to launch the next item. Hand-rolled, no new dependency
 * (Architecture Decision 3).
 */
function runConcurrentQueue(
  items: number[],
  concurrency: number,
  worker: (item: number) => Promise<void>
): void {
  let nextIndex = 0;

  const launchNext = () => {
    if (nextIndex >= items.length) return;
    const item = items[nextIndex];
    nextIndex += 1;
    worker(item).finally(launchNext);
  };

  const initialLaunchCount = Math.min(concurrency, items.length);
  for (let i = 0; i < initialLaunchCount; i += 1) {
    launchNext();
  }
}

/**
 * Fuzzy-suggestion pane for the "Link to Source" modal (Sub-task 9.3).
 *
 * There is no real server-push here — Task 7.2's suggestion endpoint is an
 * explicitly-labeled FALLBACK (see `api/routes/externalLinkSearch.js`'s
 * header): one HTTP request per product name variant, index-addressed via
 * `?variantIndex=`. This component fires `variantIndex = 0` first to learn
 * `total_variants` from the server (the server owns the distinct-variant
 * list, never the client), then fires every remaining index CONCURRENTLY,
 * updating this component's own state independently inside each request's
 * `.then`/`.catch` — never `Promise.all`-ing and rendering once at the end —
 * so each variant's group appears in the table as soon as ITS OWN request
 * resolves.
 *
 * THE TRAP (documented risk, High): a variant with no qualifying match and a
 * variant whose request outright failed must never collapse onto the same
 * "nothing to show" code path. They are tracked as two distinct
 * `VariantSlot.status` values (`'success'` with `results: []` vs.
 * `'error'`) and rendered from two distinct branches in `buildTableRows` —
 * an empty-results variant contributes NO row at all (never even considered
 * for display), while an error-status variant ALWAYS contributes exactly
 * one visibly-marked-failed row, unconditionally (not subject to the
 * CANDIDATE_CAP truncation below, since it isn't a suggestion). See this
 * file's tests for the two states side by side.
 */
const ExternalLinkSuggestionsPane: React.FC<
  ExternalLinkSuggestionsPaneProps
> = ({
  productId,
  source,
  sourceLabel,
  stagedKeys,
  onToggleSuggestionGroup,
}) => {
  const api = useApi();
  const productService = useMemo(() => new ProductService(api), [api]);

  const [status, setStatus] = useState<PaneStatus>('loading-first');
  const [totalVariants, setTotalVariants] = useState<number | null>(null);
  const [candidateCap, setCandidateCap] = useState<number | null>(null);
  const [variantSlots, setVariantSlots] = useState<Record<number, VariantSlot>>(
    {}
  );

  // Guards state updates from a fetch that resolves after this pane has
  // unmounted or moved on to a different product/source — the classic
  // stale-closure-after-async-resolve bug, not a timeout mechanism itself
  // (the timeout that makes the loading indicator terminate unambiguously
  // is the API client's own request timeout — see below).
  const cancelledRef = useRef(false);

  const fetchVariant = useCallback(
    (index: number): Promise<void> => {
      // PERF-003 review fix: runConcurrentQueue can still invoke a queued
      // variant's fetch after this pane has unmounted (or moved to a
      // different product/source) -- without this guard, that would call
      // setVariantSlots on an unmounted component.
      if (cancelledRef.current) return Promise.resolve();

      setVariantSlots(prev => ({
        ...prev,
        [index]: {
          status: 'loading',
          variantName: prev[index]?.variantName ?? null,
          results: [],
        },
      }));

      return productService
        .getExternalLinkSuggestions(productId, source, index)
        .then(response => {
          if (cancelledRef.current) return;
          const data = response.data;

          if (index === 0) {
            setTotalVariants(data.total_variants);
            setCandidateCap(data.candidate_cap);
            if (data.total_variants === 0) {
              setStatus('no-variants');
              return;
            }
            setStatus('ready');
            const remainingIndices = Array.from(
              { length: data.total_variants - 1 },
              (_, i) => i + 1
            );
            runConcurrentQueue(
              remainingIndices,
              MAX_CONCURRENT_VARIANT_REQUESTS,
              fetchVariant
            );
          }

          setVariantSlots(prev => ({
            ...prev,
            [index]: {
              status: 'success',
              variantName: data.variant,
              results: data.results,
            },
          }));
        })
        .catch((err: unknown) => {
          if (cancelledRef.current) return;
          // Every request made through the shared API client carries its
          // own request timeout (client.ts), so a "hung" variant request
          // still eventually rejects here rather than staying pending
          // forever — this is what makes the loading indicator's "any
          // request still pending" derivation terminate unambiguously
          // rather than needing a separate timer in this component.
          const message =
            err instanceof Error ? err.message : DEFAULT_ERROR_MESSAGE;
          if (index === 0) {
            setStatus('first-variant-error');
          }
          setVariantSlots(prev => ({
            ...prev,
            [index]: {
              status: 'error',
              variantName: prev[index]?.variantName ?? null,
              results: [],
              errorMessage: message,
            },
          }));
        });
    },
    [productService, productId, source]
  );

  // `fetchVariant`'s identity is NOT a dependency of the mount effect below
  // — only `productId`/`source` (the real semantic trigger) are. `useApi()`
  // is not guaranteed to return a referentially-stable value across every
  // render (its context value is a fresh object per Provider render), so
  // `productService` — and therefore `fetchVariant`, which closes over it —
  // can change identity on renders that have nothing to do with a new
  // product or source. Depending on `fetchVariant` directly would re-run
  // this effect (and re-fire variantIndex=0) on every such render, an
  // infinite mount -> fetch -> setState -> render -> mount loop confirmed
  // via this file's own test run (`Maximum update depth exceeded`). A ref
  // always holds the latest closure without being a reactive dependency.
  const fetchVariantRef = useRef(fetchVariant);
  fetchVariantRef.current = fetchVariant;

  useEffect(() => {
    cancelledRef.current = false;
    setStatus('loading-first');
    setTotalVariants(null);
    setCandidateCap(null);
    setVariantSlots({});
    fetchVariantRef.current(0);
    return () => {
      cancelledRef.current = true;
    };
  }, [productId, source]);

  const handleRetryFirst = () => {
    cancelledRef.current = false;
    setStatus('loading-first');
    setTotalVariants(null);
    setCandidateCap(null);
    setVariantSlots({});
    fetchVariant(0);
  };

  // Visible while ANY fired request (0..total_variants-1) hasn't settled
  // yet — derived purely from variantSlots' own statuses, never a
  // separately-tracked boolean that could drift from reality.
  const isSearching = useMemo(() => {
    if (status === 'loading-first') return true;
    if (totalVariants === null) return false;
    for (let i = 0; i < totalVariants; i += 1) {
      const slot = variantSlots[i];
      if (!slot || slot.status === 'loading') return true;
    }
    return false;
  }, [status, totalVariants, variantSlots]);

  const tableRows = useMemo<TableRow[]>(() => {
    if (totalVariants === null) return [];

    // Flatten every successfully-resolved variant's groups, tagged by
    // variant, to apply Task 4's CANDIDATE_CAP over the WHOLE pane (not
    // per variant) — docs/API.md: the cap "is defined per source pane...
    // the caller applies it once after accumulating results across all
    // variantIndex calls for a pane." Failed variants are deliberately
    // NOT part of this flattened, cappable list (see below).
    const allRows: Array<{
      variantIndex: number;
      variantName: string;
      group: ExternalLinkSuggestionResult;
      uid: string;
    }> = [];
    for (let i = 0; i < totalVariants; i += 1) {
      const slot = variantSlots[i];
      if (slot?.status === 'success') {
        slot.results.forEach((group, groupIdx) => {
          allRows.push({
            variantIndex: i,
            variantName: slot.variantName ?? `Variant ${i + 1}`,
            group,
            uid: `${i}-${groupIdx}`,
          });
        });
      }
    }

    // Resolve record_id ownership across the WHOLE pane before capping or
    // rendering: the same physical record can legitimately appear in more
    // than one row's record_ids (matched via a different matched_field, or
    // via a different name variant) — the server's dedup in
    // scoreCandidatesAgainstVariant only collapses duplicates WITHIN one
    // (matched_field, variant) pair, not across them. Left alone, checking
    // one row stages a record_id that then makes every OTHER row
    // containing it read as fully checked too, since isGroupChecked below
    // is `group.record_ids.every(...)` — "check one, several light up."
    // Give each record_id to exactly one row (its highest-scoring one) and
    // strip it from every other row's record_ids/records/record_count so a
    // group can never be a subset of what another row's checkbox staged.
    const bestRowForRecord = new Map<number, { uid: string; score: number }>();
    allRows.forEach(({ group, uid }) => {
      group.record_ids.forEach(recordId => {
        const current = bestRowForRecord.get(recordId);
        if (!current || group.score > current.score) {
          bestRowForRecord.set(recordId, { uid, score: group.score });
        }
      });
    });

    const dedupedRows = allRows
      .map(row => {
        const keptIds = row.group.record_ids.filter(
          recordId => bestRowForRecord.get(recordId)?.uid === row.uid
        );
        if (keptIds.length === row.group.record_ids.length) return row;
        const keptIdSet = new Set(keptIds);
        return {
          ...row,
          group: {
            ...row.group,
            record_ids: keptIds,
            records: row.group.records.filter(r => keptIdSet.has(r.record_id)),
            record_count: keptIds.length,
          },
        };
      })
      .filter(row => row.group.record_ids.length > 0);

    const cap = candidateCap ?? dedupedRows.length;
    const survivorUids = new Set(
      [...dedupedRows]
        .sort((a, b) => b.group.score - a.group.score)
        .slice(0, cap)
        .map(r => r.uid)
    );
    const survivors = dedupedRows.filter(r => survivorUids.has(r.uid));

    const groupedSurvivors = new Map<
      number,
      { variantName: string; groups: ExternalLinkSuggestionResult[] }
    >();
    survivors.forEach(r => {
      const existing = groupedSurvivors.get(r.variantIndex);
      if (existing) {
        existing.groups.push(r.group);
      } else {
        groupedSurvivors.set(r.variantIndex, {
          variantName: r.variantName,
          groups: [r.group],
        });
      }
    });

    // Failed variants ALWAYS surface, unconditionally, never subject to
    // the cap above — THE TRAP this sub-task is named for: an empty
    // variant (no row, ever) and a failed variant (always exactly one
    // row) must never share a code path.
    const failedByVariant = new Map<number, string>();
    for (let i = 0; i < totalVariants; i += 1) {
      const slot = variantSlots[i];
      if (slot?.status === 'error') {
        failedByVariant.set(i, slot.errorMessage ?? DEFAULT_ERROR_MESSAGE);
      }
    }

    const variantIndexesToShow = new Set<number>([
      ...Array.from(groupedSurvivors.keys()),
      ...Array.from(failedByVariant.keys()),
    ]);

    const rows: TableRow[] = [];
    Array.from(variantIndexesToShow)
      .sort((a, b) => a - b)
      .forEach(variantIndex => {
        const grouped = groupedSurvivors.get(variantIndex);
        const failedMessage = failedByVariant.get(variantIndex);
        const variantSlot = variantSlots[variantIndex];
        const variantLabel =
          grouped?.variantName ??
          variantSlot?.variantName ??
          `Variant ${variantIndex + 1}`;
        let isFirstRowOfVariant = true;

        if (grouped) {
          grouped.groups.forEach((group, idx) => {
            rows.push({
              key: `${variantIndex}-s-${idx}`,
              variantLabel: isFirstRowOfVariant ? variantLabel : null,
              kind: 'suggestion',
              group,
            });
            isFirstRowOfVariant = false;
          });
        }

        if (failedMessage !== undefined) {
          rows.push({
            key: `${variantIndex}-failed`,
            variantLabel: isFirstRowOfVariant ? variantLabel : null,
            kind: 'failed',
            errorMessage: failedMessage,
          });
        }
      });

    return rows;
  }, [totalVariants, variantSlots, candidateCap]);

  const isGroupChecked = (group: ExternalLinkSuggestionResult): boolean =>
    group.record_ids.every(recordId =>
      stagedKeys.has(getStagedRecordKey({ source, record_id: recordId }))
    );

  return (
    <div style={styles.container}>
      <span style={styles.label}>Suggested matches for {sourceLabel}</span>

      <div
        style={styles.resultsArea}
        role="region"
        aria-live="polite"
        aria-label={`${sourceLabel} suggested matches`}>
        {status === 'loading-first' && (
          <div style={styles.stateMessage} role="status">
            Searching name variants for suggested matches…
          </div>
        )}

        {status === 'first-variant-error' && (
          <div style={styles.errorMessage} role="alert">
            <span>
              Could not load suggestions for this product&apos;s name variants.
            </span>
            <button
              type="button"
              style={styles.retryButton}
              onClick={handleRetryFirst}>
              Retry
            </button>
          </div>
        )}

        {status === 'no-variants' && (
          <div style={styles.stateMessage}>
            This product has no active name variants to suggest matches for.
          </div>
        )}

        {status === 'ready' && (
          <>
            {isSearching && (
              <div style={styles.searchingBanner} role="status">
                Searching remaining name variants…
              </div>
            )}

            {tableRows.length === 0 && !isSearching && (
              <div style={styles.stateMessage}>
                No qualifying suggestions found across {totalVariants ?? 0} name
                variant
                {totalVariants === 1 ? '' : 's'}.
              </div>
            )}

            {tableRows.length > 0 && (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th} aria-label="Add to review list" />
                    <th style={styles.th}>Name Variant</th>
                    <th style={styles.th}>Candidate Match</th>
                    <th style={styles.th}>
                      <span style={styles.matchHeader}>
                        Match
                        <InfoTooltip
                          text="This percentage measures name similarity only, not chemical equivalence. Visually similar names can still represent different chemical entities (different salts, hydrates, or strengths) — review each match before linking; never treat this score as an automatic-link signal."
                          size={13}
                          ariaLabel="What does the Match percentage mean?"
                          zIndex={10003}
                        />
                      </span>
                    </th>
                    <th style={styles.th}>Records</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map(row => {
                    if (row.kind === 'failed') {
                      return (
                        <tr key={row.key} style={styles.failedRow}>
                          <td style={styles.td} />
                          <td style={styles.td}>{row.variantLabel}</td>
                          <td
                            style={{ ...styles.td, color: COLOR_DANGER_TEXT }}
                            colSpan={3}
                            role="alert">
                            Failed to load — {row.errorMessage}
                          </td>
                        </tr>
                      );
                    }

                    const { group } = row;
                    if (!group) return null; // unreachable — every 'suggestion' row carries a group
                    const checked = isGroupChecked(group);
                    const toggle = (nextChecked: boolean) =>
                      onToggleSuggestionGroup(
                        {
                          matched_field: group.matched_field,
                          display_name: group.display_name,
                          record_ids: group.record_ids,
                          records: group.records,
                        },
                        nextChecked
                      );
                    return (
                      <tr
                        key={row.key}
                        style={styles.suggestionRow}
                        // tabIndex/onKeyDown make the row keyboard-operable
                        // (PR #719 review follow-up) without overriding its
                        // implicit ARIA "row" role -- setting role="checkbox"
                        // here would break every getByRole('row') query
                        // against this table, screen-reader table
                        // navigation included. The <input type="checkbox">
                        // in the first cell already carries the correct
                        // checked/label semantics.
                        tabIndex={0}
                        onClick={() => toggle(!checked)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggle(!checked);
                          }
                        }}>
                        <td style={styles.td}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => toggle(e.target.checked)}
                            onClick={e => e.stopPropagation()}
                            // Enter/Space on the checkbox itself must not
                            // bubble into the row's onKeyDown (PR #719
                            // review follow-up) -- otherwise the row's
                            // preventDefault()+toggle() double-handles the
                            // same key press the checkbox's own native
                            // toggle already responds to.
                            onKeyDown={e => e.stopPropagation()}
                            aria-label={`Add ${group.display_name} to review list`}
                          />
                        </td>
                        <td style={styles.td}>{row.variantLabel}</td>
                        <td style={styles.td}>{group.display_name}</td>
                        <td style={styles.td}>{group.score}%</td>
                        <td style={styles.td}>{group.record_count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: GRAY_500,
  },
  resultsArea: {
    flex: 1,
    minHeight: '12rem',
    maxHeight: '24rem',
    overflowY: 'auto',
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    padding: '0.5rem',
  },
  stateMessage: {
    padding: '1rem',
    fontSize: '0.8125rem',
    color: GRAY_500,
    textAlign: 'center',
  },
  errorMessage: {
    padding: '1rem',
    fontSize: '0.8125rem',
    color: COLOR_DANGER_TEXT,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
  },
  retryButton: {
    padding: '4px 12px',
    fontSize: '0.8125rem',
    border: `1px solid ${COLOR_DANGER}`,
    borderRadius: '4px',
    background: '#fff',
    color: COLOR_DANGER_TEXT,
    cursor: 'pointer',
  },
  searchingBanner: {
    padding: '0.375rem 0.5rem',
    fontSize: '0.75rem',
    color: GRAY_500,
    fontStyle: 'italic',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8125rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.375rem 0.5rem',
    borderBottom: `1px solid ${GRAY_300}`,
    color: GRAY_500,
    fontWeight: 600,
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.375rem 0.5rem',
    borderBottom: `1px solid ${GRAY_200}`,
    color: GRAY_900,
    verticalAlign: 'top',
  },
  matchHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  failedRow: {
    backgroundColor: GRAY_50,
  },
  suggestionRow: {
    cursor: 'pointer',
  },
};

export default ExternalLinkSuggestionsPane;
